import "./style.css";
import { readContent } from "./engine/content";
import { RemoteContent } from "./engine/remoteContent";
import { configFromRelease, defaultConfig } from "./game/core/config";
import { parseLevelPack, type LevelPack } from "./game/core/levelPack";
import { Progress, type SaveData } from "./game/core/progress";
import { ArrowMasterApp } from "./game/view/ArrowMasterApp";
import { AudioManager, readAudioPrefs } from "./game/view/audio/audioManager";
import { connectionText } from "./game/view/connectionText";
import { LoadingView } from "./game/view/LoadingView";
import { Analytics } from "./platform/analytics";
import { loadAppConfig } from "./platform/appConfig";
import { getClientInfo } from "./platform/clientInfo";
import { deviceId } from "./platform/deviceId";
import { envStore } from "./platform/envStore";
import { onActiveChange, ready, vibrate } from "./platform/native";
import { EnvPin, withEnv } from "./platform/net/env";
import type { Content } from "./platform/net/protocol";
import { ServerSave } from "./platform/net/serverSave";
import { Session } from "./platform/net/session";
import { GameSocket } from "./platform/net/socket";
import { loadJson, saveJson } from "./platform/storage";

const SAVE_KEY = "save";
const AUDIO_PREFS_KEY = "audio";
const SAVE_DELAY_MS = 1000;
/** Used when app-config.json is missing or invalid. */
const DEFAULT_WS_URL = "ws://localhost:8080/v1/ws";
/** Spreads config.get calls after a publish so clients don't all fetch at once. */
const CONFIG_REFRESH_JITTER_MS = 3000;
/** The first level download holds the game back at most this long; then the built-in levels are used. */
const CONTENT_TIMEOUT_MS = 10_000;
/** OTA content: the Level Studio's published levels (alias `catalog`, JSON). */
const LEVELS_BUNDLE = "levels";
const LEVELS_ALIAS = "catalog";
/** OTA content: lobby art (alias `logo`, an image). */
const ART_BUNDLE = "main";
const LOGO_ALIAS = "logo";

let pendingSave: SaveData | undefined;
let saveTimer: ReturnType<typeof setTimeout> | undefined;
/** The server copy of the save (state.save); set once the socket exists. */
let serverSave: ServerSave | undefined;

/** Batches frequent progress changes into one write per second. */
function scheduleSave(data: SaveData): void {
  pendingSave = data;
  saveTimer ??= setTimeout(flushSave, SAVE_DELAY_MS);
}

function flushSave(): void {
  clearTimeout(saveTimer);
  saveTimer = undefined;
  if (!pendingSave) return;
  const data = pendingSave;
  void saveJson(SAVE_KEY, data).catch((err: unknown) => console.error("save failed", err));
  serverSave?.save({ ...data });
  pendingSave = undefined;
}

/** The published levels from loaded content, or undefined (built-in levels) when missing or invalid. */
function levelPackFrom(loaded: Record<string, unknown> | undefined): LevelPack | undefined {
  const value = loaded?.[LEVELS_ALIAS];
  if (value === undefined) return undefined;
  try {
    return parseLevelPack(value);
  } catch (err) {
    console.error("content: invalid level pack, using built-in levels", err);
    return undefined;
  }
}

/** The lobby logo's URL from the release's content, if it has one. */
function logoFrom(content: Content | undefined): string | undefined {
  return readContent(content)[ART_BUNDLE]?.find((asset) => asset.alias === LOGO_ALIAS)?.url;
}

async function boot(): Promise<void> {
  const container = document.getElementById("game");
  if (!container) throw new Error("#game element missing");
  const bootStart = performance.now();
  const loading = new LoadingView(container);

  const [appConfig, clientInfo] = await Promise.all([loadAppConfig({ wsUrl: DEFAULT_WS_URL }), getClientInfo()]);
  const wsUrl = import.meta.env.VITE_BACKEND_WS_URL || appConfig.wsUrl;
  const env = new EnvPin(envStore, appConfig.env);
  await env.load();
  const socket = new GameSocket({ url: () => withEnv(wsUrl, env.current) });
  const session = new Session(socket, deviceId, clientInfo, env);
  const analytics = new Analytics(socket, session);
  analytics.track("app_open", { cold_start: true });

  // Deadlines and the Daily Challenge day follow the server's clock, not the device's.
  let clockOffset = 0;
  const now = () => Date.now() + clockOffset;

  let app: ArrowMasterApp | undefined;
  let config = defaultConfig;
  let configVersion: number | undefined;
  const applyConfig = ({ version, data }: { version: number; data: unknown }) => {
    config = configFromRelease(data);
    app?.setConfig(config);
    if (version !== configVersion) analytics.track("config_applied", { version });
    configVersion = version;
  };

  // Content changes run one at a time, always towards the newest content seen.
  const content = new RemoteContent();
  let latestContent: Content | undefined;
  let contentQueue = Promise.resolve();
  const syncContent = () => {
    contentQueue = contentQueue
      .then(async () => {
        app?.setLogo(logoFrom(latestContent));
        const changed = content.update(latestContent);
        if (changed.includes(LEVELS_BUNDLE) || content.needsLoad(LEVELS_BUNDLE)) {
          const pack = levelPackFrom(await content.load(LEVELS_BUNDLE));
          app?.setLevels(pack);
          console.info(`content: ${pack ? `levels ${pack.revision.slice(0, 8)}` : "built-in levels"}`);
        }
        await content.release();
      })
      .catch((err: unknown) => console.error("content update failed", err));
  };

  session.on("state", (state) => {
    const text = connectionText(state);
    if (app) app.setConnection(state.kind === "ready" || state.kind === "paused", text, state.kind === "fatal" && state.reason.kind === "replaced" ? () => session.retry() : undefined);
    else loading.setStatus(text);
  });
  serverSave = new ServerSave(socket);
  let serverState: { version: number; data: unknown } | undefined;
  const firstReady = new Promise<void>((resolve) => session.on("ready", () => resolve()));
  session.on("ready", (bootstrap) => {
    clockOffset = bootstrap.serverTime - Date.now();
    serverSave?.setVersion(bootstrap.state.version);
    serverState ??= bootstrap.state;
    applyConfig(bootstrap.config);
    latestContent = bootstrap.config.content;
    if (app) syncContent();
  });
  // Tunables are safe to change at any time: in-flight attempts keep their timer, lives and board.
  session.on("configPending", () => {
    setTimeout(() => {
      session.refreshConfig().catch((err: unknown) => console.warn("config refresh failed", err));
    }, Math.random() * CONFIG_REFRESH_JITTER_MS);
  });
  session.on("config", (next) => {
    applyConfig(next);
    latestContent = next.content;
    if (app) syncContent();
  });

  onActiveChange((active) => {
    analytics.track(active ? "app_foreground" : "app_background");
    // Before the session pauses, so the batch still goes out on this connection.
    analytics.setActive(active);
    session.setActive(active);
    if (!active) flushSave();
  });
  // The loading view is up: hide the native splash screen.
  await ready();
  session.start();

  const [saved, audioPrefs] = await Promise.all([loadJson(SAVE_KEY), loadJson(AUDIO_PREFS_KEY)]);
  loading.setProgress(0.2);
  await firstReady;
  loading.setProgress(0.4);
  // Never blocked by the CDN: on errors or timeout the game starts with the built-in levels.
  const firstContent = latestContent;
  content.update(firstContent);
  const levels = levelPackFrom(await content.load(LEVELS_BUNDLE, (p) => loading.setProgress(0.4 + p * 0.6), CONTENT_TIMEOUT_MS));
  loading.setProgress(1);

  // The server's save wins once it has one; the local save is the offline fallback.
  const start = serverState && serverState.version > 0 ? serverState.data : saved;
  const progress = new Progress(start, config, levels?.catalog, {
    now,
    newId: () => crypto.randomUUID(),
    onChange: scheduleSave,
  });
  const audio = new AudioManager({
    prefs: readAudioPrefs(audioPrefs),
    savePrefs: (prefs) => void saveJson(AUDIO_PREFS_KEY, prefs).catch((err: unknown) => console.error("saving audio settings failed", err)),
    vibrate,
  });
  app = new ArrowMasterApp({
    progress,
    audio,
    now,
    track: (name, props) => analytics.track(name, props),
    levels,
    logoUrl: logoFrom(firstContent),
  });
  loading.destroy();
  await app.init(container);
  analytics.track("game_ready", { load_ms: Math.round(performance.now() - bootStart), content: levels ? "remote" : "built_in" });
  // Content that arrived during the first download.
  if (latestContent !== firstContent) syncContent();
}

boot().catch((err: unknown) => {
  console.error("boot failed", err);
  const container = document.getElementById("game");
  if (container) container.textContent = `Unable to start Arrow Master: ${String(err)}`;
  // Never leave the native splash screen stuck on top of an error.
  void ready();
});
